<template>
  <div class="mover-biomasa-evento container-fluid mt-4">
    <h2>Mover Biomasa al Evento</h2>
    <p class="text-muted small">
      Seleccioná las biomasa(s) que quieras asignar al evento actual. Podés filtrar
      por tipo o descripción y navegar con paginación.
    </p>

    <!-- Mensaje de advertencia -->
    <div class="alert alert-warning d-flex align-items-center" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      <div>
        Al confirmar, las biomasa seleccionadas pasarán a formar parte del evento actual.
      </div>
    </div>

    <!-- Formulario de fecha y observaciones generales -->
    <div class="row g-2 align-items-center mb-3">
      <div class="col-auto">
        <label for="fechaInicio" class="form-label mb-0 small">Fecha de Inicio:</label>
        <input id="fechaInicio" type="date" v-model="fecha_inicio" class="form-control form-control-sm" />
      </div>
      <div class="col">
        <label for="obsGeneral" class="form-label mb-0 small">Observaciones (opcional):</label>
        <input id="obsGeneral" type="text" v-model="observacionesGenerales" class="form-control form-control-sm"
          placeholder="Comentario para todas las biomasa seleccionadas" />
      </div>
    </div>

    <!-- Filtros y acciones masivas -->
    <div class="d-flex flex-wrap gap-2 align-items-center my-3">
      <input v-model="filterText" type="text" class="form-control form-control-sm"
        placeholder="Filtrar por tipo u observaciones..." style="max-width: 250px;" />
      <button class="btn btn-secondary btn-sm" @click="clearFilters">
        Limpiar Filtros
      </button>
      <button class="btn btn-outline-primary btn-sm" @click="selectAllVisible">
        Seleccionar Todo ({{ paginatedBiomasa.length }})
      </button>
      <button class="btn btn-outline-danger btn-sm" @click="deselectAll">
        Deseleccionar Todo
      </button>
    </div>

    <!-- ...dentro del template reemplazá esta parte -->

    <!-- Lista de biomasa -->
    <div v-if="paginatedBiomasa.length" class="row g-2">
      <div v-for="b in paginatedBiomasa" :key="b.id" class="col-12 col-sm-6 col-md-4">
        <div class="card p-2 cursor-pointer"
          :class="{ 'border-primary': isSelected(b.id), 'selected': isSelected(b.id) }" @click="toggleSelection(b.id)">
          <div class="card-body p-2">
            <h5 class="card-title mb-1">{{ b.tipo_biomasa }} #{{ b.id }}</h5>
            <p class="card-text mb-1">Peso: {{ b.peso }} {{ b.unidad_medida }}</p>
            <p class="card-text small text-muted mb-1">
              Lote:
              <span v-if="b.planta_biomasa?.length">
                {{ b.planta_biomasa[0].planta.lote.nombre }}
                <small>({{ b.planta_biomasa[0].planta.lote.codigo }})</small>
              </span>
              <span v-else>—</span>
            </p>
            <p class="card-text small text-muted mb-1">
              Cosecha: {{ formatDate(b.fecha_cosecha) }}
            </p>
            <p class="card-text small text-muted">
              Obs.: {{ b.observaciones || '—' }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Mensaje si no hay biomasa -->
    <div v-else class="alert alert-info small text-center">
      No se encontraron biomasas disponibles para mover.
      <br />
      <span class="text-muted">
        Puede deberse a que:
        <ul class="text-start mt-2 mb-0 small">
          <li>Ya están asignadas a este evento</li>
          <li>Ya fueron transformadas en materia prima</li>
          <li>Están marcadas como listas para producción</li>
        </ul>
      </span>
    </div>


    <!-- Paginación -->
    <nav v-if="totalPages > 1" class="mt-3">
      <ul class="pagination pagination-sm justify-content-center">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <a class="page-link" href="#" @click.prevent="prevPage">Anterior</a>
        </li>
        <li v-for="p in visiblePages" :key="p" class="page-item" :class="{ active: currentPage === p }">
          <a class="page-link" href="#" @click.prevent="goToPage(p)">{{ p }}</a>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <a class="page-link" href="#" @click.prevent="nextPage">Siguiente</a>
        </li>
      </ul>
    </nav>

    <!-- Confirmar movimiento -->
    <div class="d-flex flex-column align-items-end mt-4">
      <div class="mb-2">
        <strong>Biomasa seleccionada:</strong> {{ selectedBiomasa.length }}
      </div>
      <button class="btn btn-primary" @click="confirmMove"
        :disabled="selectedBiomasa.length === 0 || proyecto_cerrado || evento_finalizado">
        Mover al Evento {{ selectedBiomasa.length }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from '@/axios';
import { mapGetters } from 'vuex';

export default {
  name: 'MoverBiomasaEvento',
  props: {
    proyecto_evento_id: { type: Number, required: true },
    proyecto_id: { type: Number, required: true }
  },
  data() {
    return {
      biomasaList: [],
      filterText: '',
      currentPage: 1,
      pageSize: 12,
      selectedBiomasa: [],
      // Inicializar fecha_inicio con fecha actual sin usar computed
      fecha_inicio: new Date().toISOString().slice(0, 10),
      observacionesGenerales: '',
      proyecto_cerrado: false,
      evento_finalizado: false
    };
  },
  computed: {
    ...mapGetters(['getClubActual']),
    filteredBiomasa() {
      let list = this.biomasaList;
      if (this.filterText) {
        const ft = this.filterText.toLowerCase();
        list = list.filter(b =>
          b.tipo_biomasa.toLowerCase().includes(ft) ||
          (b.observaciones && b.observaciones.toLowerCase().includes(ft))
        );
      }
      return list;
    },
    paginatedBiomasa() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredBiomasa.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.ceil(this.filteredBiomasa.length / this.pageSize) || 1;
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      let start = Math.max(1, this.currentPage - 2);
      let end = Math.min(total, start + 4);
      if (end - start < 4) start = Math.max(1, end - 4);
      for (let p = start; p <= end; p++) pages.push(p);
      return pages;
    }
  },
  mounted() {
    this.proyecto_cerrado = this.$store.getters.getProyectoActual.proyecto_cerrado;
    this.evento_finalizado = this.$store.getters.getEventoActual.evento_finalizado;
    this.fetchBiomasa();
  },
  methods: {
    async fetchBiomasa() {
      try {
        const club_id = this.getClubActual.id;
        const res = await axios.get('/api/biomasa_not_event', {
          params: { club_id, proyecto_id: this.proyecto_id, proyecto_evento_id: this.proyecto_evento_id }
        });
        this.biomasaList = res.data;
      } catch (e) {
        console.error('Error obteniendo biomasa:', e);
      }
    },
    toggleSelection(id) {
      const idx = this.selectedBiomasa.indexOf(id);
      if (idx === -1) this.selectedBiomasa.push(id);
      else this.selectedBiomasa.splice(idx, 1);
    },
    isSelected(id) {
      return this.selectedBiomasa.includes(id);
    },
    clearFilters() {
      this.filterText = '';
      this.currentPage = 1;
    },
    selectAllVisible() {
      this.paginatedBiomasa.forEach(b => {
        if (!this.isSelected(b.id)) this.selectedBiomasa.push(b.id);
      });
    },
    deselectAll() {
      this.selectedBiomasa = [];
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    goToPage(p) {
      this.currentPage = p;
    },
    async confirmMove() {
      if (!confirm(`Mover ${this.selectedBiomasa.length} biomasa(s) al evento?`)) return;
      try {
        await axios.post('/api/biomasa-eventos', {
          proyecto_evento_id: this.proyecto_evento_id,
          biomasa_ids: this.selectedBiomasa,
          fecha_inicio: this.fecha_inicio,
          observaciones: this.observacionesGenerales || null
        });
        alert('Biomasa movida exitosamente');
        this.selectedBiomasa = [];
        this.fetchBiomasa();
      } catch (e) {
        console.error('Error moviendo biomasa:', e);
        alert('Error al mover biomasa');
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      return d.toLocaleDateString();
    }
  }
};
</script>

<style scoped>
.mover-biomasa-evento {
  font-size: 0.9rem;
}

.cursor-pointer {
  cursor: pointer;
}

.selected {
  background-color: #d1e7dd;
  border-color: #0f5132;
}

.pagination {
  margin-top: 10px;
}
</style>